import Cookies from 'js-cookie';
import { domain } from '../configuration/Environment';

const IN_APP_KEY = 'inApp';
const IN_APP_VALUE = '1';
const IN_APP_TS_KEY = 'inAppAt';

// Validity window for the durable fallback. Long enough to outlast a full-page
// SSO/OAuth round-trip (Google/Apple consent + 2FA), short enough that an
// unrelated later browser session on the same storage partition is NOT treated
// as in-app. sessionStorage remains the authoritative signal within an active
// session; this only bounds the fallback used when sessionStorage was dropped
// during the round-trip.
const IN_APP_TTL_MS = 30 * 60 * 1000; // 30 minutes
const IN_APP_TTL_DAYS = IN_APP_TTL_MS / (24 * 60 * 60 * 1000);

type StorageKind = 'local' | 'session';

// Acquiring window.localStorage / window.sessionStorage can ITSELF throw a
// SecurityError when storage is disabled or blocked (e.g. Safari with cookies
// blocked, some private modes). The property access is wrapped here so it
// cannot crash callers — wrapping only getItem/setItem would be too late
// because the getter runs when the argument is evaluated.
const getStorage = (kind: StorageKind): Storage | undefined => {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return undefined;
  }
};

const safeGet = (kind: StorageKind, key: string): string | null => {
  try {
    return getStorage(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const safeSet = (kind: StorageKind, key: string, value: string): void => {
  try {
    getStorage(kind)?.setItem(key, value);
  } catch {
    // Storage unavailable / over quota — best effort only.
  }
};

const safeRemove = (kind: StorageKind, key: string): void => {
  try {
    getStorage(kind)?.removeItem(key);
  } catch {
    // Best effort only.
  }
};

// Cookie layer, scoped to the registrable domain (`.<domain>`). localStorage is
// origin-scoped, so it does NOT survive an SSO round-trip that returns to a
// different subdomain (e.g. opened on app.<domain>, returns via auth.<domain>);
// a domain-scoped cookie is readable across all subdomains and tends to persist
// across iOS WKWebView cross-origin navigation better than localStorage. Its
// own expiry enforces the TTL. Wrapped because cookie access can throw.
const cookieAttrs = (): Cookies.CookieAttributes => ({
  domain: domain ? `.${domain}` : undefined,
  path: '/',
  secure: true,
  sameSite: 'lax',
});

const readCookieFlag = (): boolean => {
  try {
    return Cookies.get(IN_APP_KEY) === IN_APP_VALUE;
  } catch {
    return false;
  }
};

const writeCookieFlag = (): void => {
  try {
    Cookies.set(IN_APP_KEY, IN_APP_VALUE, { ...cookieAttrs(), expires: IN_APP_TTL_DAYS });
  } catch {
    // Best effort only.
  }
};

const removeCookieFlag = (): void => {
  try {
    Cookies.remove(IN_APP_KEY, cookieAttrs());
  } catch {
    // Best effort only.
  }
};

// Refreshes the durable fallback (localStorage + cookie) and slides the expiry
// window forward.
const refreshDurableFlag = (): void => {
  safeSet('local', IN_APP_KEY, IN_APP_VALUE);
  safeSet('local', IN_APP_TS_KEY, String(Date.now()));
  writeCookieFlag();
};

const hasFreshDurableFlag = (): boolean => {
  // A present cookie is fresh by definition (the browser drops it once expired)
  // and survives cross-subdomain round-trips, so it is checked first.
  if (readCookieFlag()) return true;
  if (safeGet('local', IN_APP_KEY) !== IN_APP_VALUE) return false;
  const ts = Number(safeGet('local', IN_APP_TS_KEY));
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts <= IN_APP_TTL_MS;
};

// Clears the in-app flag from every store. Exposed so explicit web entry
// points (e.g. logout / "open in browser") can drop stale state.
export const clearInAppFlag = (): void => {
  if (typeof window === 'undefined') return;
  safeRemove('session', IN_APP_KEY);
  safeRemove('local', IN_APP_KEY);
  safeRemove('local', IN_APP_TS_KEY);
  removeCookieFlag();
};

// Detects whether the web app is running inside the native app webview.
//
// The native app opens the web app with `?inApp=1`. We persist that flag so it
// survives later navigations, including full-page SSO round-trips where the
// backend redirect returns without the query param, sessionStorage may have
// been dropped in a separate browsing context, and the return may land on a
// different subdomain than the original entry.
//
// Precedence:
//   1. An explicit signal in the current URL wins over stored state: `?inApp=1`
//      marks in-app; any other explicit `inApp` value (e.g. `?inApp=0`) is an
//      explicit web entry that clears stored state.
//   2. Otherwise sessionStorage is authoritative for the active session.
//   3. Otherwise fall back to the time-bounded durable flag (cookie spanning
//      subdomains, then localStorage), and restore sessionStorage from it.
//      Expired/absent durable flags are cleaned up so stale state cannot
//      accumulate.
export const isInApp = (): boolean => {
  if (typeof window === 'undefined') return false;

  const param = new URLSearchParams(window.location.search).get(IN_APP_KEY);
  if (param === IN_APP_VALUE) {
    safeSet('session', IN_APP_KEY, IN_APP_VALUE);
    refreshDurableFlag();
    return true;
  }
  if (param != null) {
    clearInAppFlag();
    return false;
  }

  if (safeGet('session', IN_APP_KEY) === IN_APP_VALUE) {
    refreshDurableFlag();
    return true;
  }

  if (hasFreshDurableFlag()) {
    safeSet('session', IN_APP_KEY, IN_APP_VALUE);
    refreshDurableFlag();
    return true;
  }

  if (safeGet('local', IN_APP_KEY) === IN_APP_VALUE) {
    clearInAppFlag();
  }

  return false;
};

export interface InAppDiagnostics {
  result: boolean;
  param: string | null;
  session: boolean;
  local: boolean;
  localAgeMs: number | null;
  cookie: boolean;
}

// Snapshot of every in-app signal, for diagnosing why the native-app handoff
// does or does not fire (notably the iOS Google-SSO round-trip). Reads the raw
// layers BEFORE calling isInApp() so the snapshot reflects pre-resolution state.
export const getInAppDiagnostics = (): InAppDiagnostics => {
  if (typeof window === 'undefined') {
    return { result: false, param: null, session: false, local: false, localAgeMs: null, cookie: false };
  }

  const param = new URLSearchParams(window.location.search).get(IN_APP_KEY);
  const session = safeGet('session', IN_APP_KEY) === IN_APP_VALUE;
  const local = safeGet('local', IN_APP_KEY) === IN_APP_VALUE;
  const ts = Number(safeGet('local', IN_APP_TS_KEY));
  const localAgeMs = local && Number.isFinite(ts) ? Date.now() - ts : null;
  const cookie = readCookieFlag();

  return { result: isInApp(), param, session, local, localAgeMs, cookie };
};
